import type { Tuning } from '../config/tuning'

/**
 * Web Audio manager: synthesized chiptune-ish SFX + a dark two-layer music
 * bed, all procedural (no assets in v1; the manifest swap point for real
 * audio later is the `play(name)` recipe table). Raw Web Audio, no library.
 */
export type SfxName =
  | 'fire'
  | 'enemyFire'
  | 'hit'
  | 'kill'
  | 'hurt'
  | 'food'
  | 'key'
  | 'treasure'
  | 'magicPickup'
  | 'magic'
  | 'genDown'
  | 'door'
  | 'teleport'
  | 'bossIntro'
  | 'bossKill'
  | 'playerDie'
  | 'levelClear'
  | 'auditor'
  | 'starving'
  | 'uiMove'
  | 'uiSelect'

/** which music bed is playing; acts map 1:1, bosses get their own */
export type MusicKey = 'act1' | 'act2' | 'act3' | 'boss'

/**
 * Per-pattern note tables — the same synth voices throughout, different
 * bones. Tempo and gain knobs live in tuning.audio.music; frequencies are
 * pitch content, not balance, so they stay here with the SFX recipes.
 * `bass` is indexed one note per half-bar slot (null = rest); `hats` are
 * beats within the 8-beat bar; `drone` sounds once every four bars.
 */
const MUSIC_PATTERNS: Record<MusicKey, { bass: (number | null)[]; hats: number[]; drone: number }> = {
  // A1 A1 C2 G1 — the original broadcast-floor bed
  act1: { bass: [55, 55, 65.41, 49], hats: [3, 7], drone: 220 },
  // A1 C2 B1 G1 — same bones, less resolution: tenser
  act2: { bass: [55, 65.41, 61.74, 49], hats: [3, 5, 7], drone: 207.65 },
  // A1 G#1 C2 F#1 — chromatic undertow on a faster clock
  act3: { bass: [55, 51.91, 65.41, 46.25], hats: [1, 3, 5, 7], drone: 233.08 },
  // D1-rooted, half the notes, twice the weight — featured encounters
  boss: { bass: [36.71, null, 36.71, 38.89], hats: [7], drone: 146.83 }
}

export class AudioManager {
  private ctx: AudioContext | null = null
  private master!: GainNode
  private sfx!: GainNode
  private music!: GainNode
  private musicTimer: number | null = null
  private musicStep = 0
  private tempoMul = 1
  /** context time the next sequencer step is due (lookahead scheduler) */
  private nextNoteTime = 0
  private musicKey: MusicKey = 'act1'
  /** pattern swap requested mid-bar; applied at the next bar boundary */
  private pendingKey: MusicKey | null = null
  private musicDucked = false
  private noiseBuf: AudioBuffer | null = null
  sfxVolume = 0.8
  musicVolume = 0.5

  constructor(private cfg: Tuning['audio']) {}

  /** must be called from a user gesture */
  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended' && !document.hidden) void this.ctx.resume()
      return
    }
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctor) return
    this.ctx = new Ctor()
    this.master = this.ctx.createGain()
    this.master.connect(this.ctx.destination)
    this.sfx = this.ctx.createGain()
    this.sfx.connect(this.master)
    this.music = this.ctx.createGain()
    this.music.connect(this.master)
    this.applyVolumes()
    // shared noise buffer
    const len = this.ctx.sampleRate * 0.5
    this.noiseBuf = this.ctx.createBuffer(1, len, this.ctx.sampleRate)
    const data = this.noiseBuf.getChannelData(0)
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1
    // hidden tab: suspend the context clock (a frozen clock can't be mangled
    // by throttled timers); on return, resume and re-seat the scheduler just
    // ahead of now so no backlog of notes fires at once
    document.addEventListener('visibilitychange', () => {
      const ctx = this.ctx
      if (!ctx) return
      if (document.hidden) {
        if (ctx.state === 'running') void ctx.suspend()
      } else if (ctx.state === 'suspended') {
        void ctx.resume()
        if (this.musicTimer !== null) this.nextNoteTime = Math.max(this.nextNoteTime, ctx.currentTime + 0.05)
      }
    })
  }

  setVolumes(sfx: number, music: number): void {
    this.sfxVolume = sfx
    this.musicVolume = music
    this.applyVolumes()
  }

  private applyVolumes(): void {
    if (!this.ctx) return
    this.sfx.gain.value = this.sfxVolume * 0.5
    this.music.gain.value = this.musicGainTarget()
  }

  private musicGainTarget(): number {
    return this.musicVolume * 0.22 * (this.musicDucked ? this.cfg.pauseDuckMul : 1)
  }

  /** pause behavior: music drops under the pause overlay, restores on resume */
  setMusicDuck(on: boolean): void {
    if (this.musicDucked === on) return
    this.musicDucked = on
    if (!this.ctx) return
    this.music.gain.cancelScheduledValues(this.ctx.currentTime)
    this.music.gain.setTargetAtTime(this.musicGainTarget(), this.ctx.currentTime, this.cfg.duckRampSeconds)
  }

  private tone(
    dest: GainNode,
    type: OscillatorType,
    f0: number,
    f1: number,
    dur: number,
    vol = 0.5,
    curve: 'exp' | 'lin' = 'exp',
    at?: number
  ): void {
    if (!this.ctx) return
    const t = at ?? this.ctx.currentTime
    const osc = this.ctx.createOscillator()
    const g = this.ctx.createGain()
    osc.type = type
    osc.frequency.setValueAtTime(Math.max(1, f0), t)
    if (curve === 'exp') osc.frequency.exponentialRampToValueAtTime(Math.max(1, f1), t + dur)
    else osc.frequency.linearRampToValueAtTime(Math.max(1, f1), t + dur)
    g.gain.setValueAtTime(vol, t)
    g.gain.exponentialRampToValueAtTime(0.001, t + dur)
    osc.connect(g)
    g.connect(dest)
    osc.start(t)
    osc.stop(t + dur + 0.02)
  }

  private noise(dest: GainNode, dur: number, vol = 0.3, filterFreq = 2000, type: BiquadFilterType = 'lowpass', at?: number): void {
    if (!this.ctx || !this.noiseBuf) return
    const t = at ?? this.ctx.currentTime
    const src = this.ctx.createBufferSource()
    src.buffer = this.noiseBuf
    src.loop = true
    const f = this.ctx.createBiquadFilter()
    f.type = type
    f.frequency.value = filterFreq
    const g = this.ctx.createGain()
    g.gain.setValueAtTime(vol, t)
    g.gain.exponentialRampToValueAtTime(0.001, t + dur)
    src.connect(f)
    f.connect(g)
    g.connect(dest)
    src.start(t)
    src.stop(t + dur + 0.02)
  }

  play(name: SfxName): void {
    if (!this.ctx) return
    switch (name) {
      case 'fire':
        this.tone(this.sfx, 'square', 620, 180, 0.07, 0.16)
        break
      case 'enemyFire':
        // quiet, low — audible warning without competing with the player's own fire
        this.tone(this.sfx, 'square', 280, 130, 0.08, 0.09)
        break
      case 'hit':
        this.noise(this.sfx, 0.06, 0.22, 2600)
        break
      case 'kill':
        this.tone(this.sfx, 'sawtooth', 300, 40, 0.18, 0.3)
        this.noise(this.sfx, 0.12, 0.18, 1200)
        break
      case 'hurt':
        this.tone(this.sfx, 'square', 190, 70, 0.16, 0.35)
        break
      case 'food':
        this.tone(this.sfx, 'sine', 520, 780, 0.09, 0.3)
        this.tone(this.sfx, 'sine', 780, 1040, 0.12, 0.25)
        break
      case 'key':
        this.tone(this.sfx, 'triangle', 880, 1320, 0.12, 0.3)
        break
      case 'treasure':
        this.tone(this.sfx, 'triangle', 660, 990, 0.08, 0.28)
        this.tone(this.sfx, 'triangle', 990, 1480, 0.14, 0.22)
        break
      case 'magicPickup':
        this.tone(this.sfx, 'sine', 440, 1760, 0.2, 0.25)
        break
      case 'magic':
        this.tone(this.sfx, 'sawtooth', 100, 1200, 0.5, 0.4, 'lin')
        this.noise(this.sfx, 0.6, 0.35, 900, 'highpass')
        break
      case 'genDown':
        this.tone(this.sfx, 'sine', 140, 30, 0.5, 0.55)
        this.noise(this.sfx, 0.4, 0.3, 500)
        break
      case 'door':
        this.tone(this.sfx, 'square', 240, 320, 0.1, 0.18)
        break
      case 'teleport':
        this.tone(this.sfx, 'sine', 300, 1500, 0.22, 0.3)
        this.tone(this.sfx, 'sine', 1500, 300, 0.22, 0.2)
        break
      case 'bossIntro':
        this.tone(this.sfx, 'sawtooth', 65, 55, 1.1, 0.5, 'lin')
        this.tone(this.sfx, 'square', 98, 65, 1.1, 0.3, 'lin')
        break
      case 'bossKill':
        this.tone(this.sfx, 'sawtooth', 220, 30, 0.9, 0.5)
        this.noise(this.sfx, 0.9, 0.4, 800)
        break
      case 'playerDie':
        this.tone(this.sfx, 'square', 400, 40, 0.8, 0.4)
        break
      case 'levelClear':
        this.tone(this.sfx, 'triangle', 523, 523, 0.12, 0.3)
        this.tone(this.sfx, 'triangle', 659, 659, 0.12, 0.3, 'exp', this.ctx.currentTime + 0.11)
        this.tone(this.sfx, 'triangle', 880, 880, 0.2, 0.3, 'exp', this.ctx.currentTime + 0.22)
        break
      case 'auditor':
        this.tone(this.sfx, 'sawtooth', 110, 92, 1.6, 0.35, 'lin')
        this.tone(this.sfx, 'sawtooth', 116, 98, 1.6, 0.35, 'lin')
        break
      case 'starving':
        // low heartbeat double-thump — the vitals warning you feel more than hear
        this.tone(this.sfx, 'sine', 58, 36, 0.22, this.cfg.starveThumpVolume)
        this.tone(this.sfx, 'sine', 52, 34, 0.18, this.cfg.starveThumpVolume * 0.7, 'exp', this.ctx.currentTime + 0.28)
        break
      case 'uiMove':
        this.tone(this.sfx, 'square', 500, 500, 0.04, 0.1)
        break
      case 'uiSelect':
        this.tone(this.sfx, 'square', 700, 900, 0.08, 0.15)
        break
    }
  }

  /**
   * Dark, minimal pulse bed: bass line + off-beat filtered hats, stepped by a
   * lookahead scheduler against the AudioContext clock — note times come from
   * ctx.currentTime, so a throttled wall-clock tick just queues further ahead
   * instead of drifting. Hidden tabs suspend the context (see unlock).
   */
  startMusic(key: MusicKey = 'act1'): void {
    this.setMusicDuck(false)
    this.musicKey = key
    this.pendingKey = null
    if (!this.ctx || this.musicTimer !== null) return
    this.musicStep = 0
    this.nextNoteTime = this.ctx.currentTime + 0.05
    const tick = () => {
      const ctx = this.ctx
      if (!ctx || ctx.state !== 'running') return
      const horizon = ctx.currentTime + this.cfg.musicScheduleAhead
      while (this.nextNoteTime < horizon) {
        this.nextNoteTime += this.scheduleStep(this.nextNoteTime)
      }
    }
    this.musicTimer = window.setInterval(tick, this.cfg.musicSchedulerInterval)
    tick()
  }

  /**
   * Swap the bed (act transitions, boss intro/kill). Takes effect at the next
   * bar boundary so the change lands musically rather than mid-phrase.
   */
  /** OVERRIDE slow-motion: stretch the sequencer step (and note lengths) while active */
  setTempoMul(mul: number): void {
    this.tempoMul = mul
  }

  setMusicPattern(key: MusicKey): void {
    if (this.musicTimer === null) this.musicKey = key
    else this.pendingKey = key === this.musicKey ? null : key
  }

  /** schedule one sequencer step at context time t; returns the step length (s) */
  private scheduleStep(t: number): number {
    const i = this.musicStep++
    const beat = i % 8
    if (beat === 0 && this.pendingKey) {
      this.musicKey = this.pendingKey
      this.pendingKey = null
    }
    const pat = MUSIC_PATTERNS[this.musicKey]
    const raw = this.cfg.music[this.musicKey]
    const k = { step: raw.step * this.tempoMul, gain: raw.gain }
    if (beat % 2 === 0) {
      const note = pat.bass[(i >> 1) % pat.bass.length]!
      if (note !== null) {
        this.tone(this.music, 'square', note, note, k.step * 0.86, 0.5 * k.gain, 'lin', t)
        this.tone(this.music, 'sine', note / 2, note / 2, k.step * 0.93, 0.6 * k.gain, 'lin', t)
      }
    }
    if (pat.hats.includes(beat)) this.noise(this.music, 0.05, 0.16 * k.gain, 5000, 'highpass', t)
    if (i % 32 === 16) this.tone(this.music, 'triangle', pat.drone, pat.drone, 1.4, 0.12 * k.gain, 'lin', t)
    return k.step
  }

  stopMusic(): void {
    if (this.musicTimer !== null) {
      clearInterval(this.musicTimer)
      this.musicTimer = null
    }
  }
}
